import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import Layout from '@/components/Layout';
import { useLanguage } from '@/contexts/LanguageContext';
import LocalizedLink from '@/components/LocalizedLink';
import { Search, MapPin, Phone, MessageCircle, Home, Car, Plane, Mountain } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const NotFound = () => {
  const location = useLocation();
  const { t, lang } = useLanguage();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const isDE = lang === 'de';

  const popularLinks = [
    { to: '/', icon: Home, label: isDE ? 'Startseite' : 'Home' },
    { to: '/services', icon: Car, label: isDE ? 'Unsere Services' : 'Our Services' },
    { to: '/services/airport-transfer', icon: Plane, label: isDE ? 'Flughafentransfer' : 'Airport Transfer' },
    { to: '/booking', icon: MessageCircle, label: isDE ? 'Taxi buchen' : 'Book a Taxi' },
    { to: '/tours', icon: Mountain, label: isDE ? 'Tagestouren' : 'Day Tours' },
    { to: '/transfers', icon: MapPin, label: isDE ? 'Beliebte Transfers' : 'Popular Transfers' },
  ];

  return (
    <Layout>
      <Helmet>
        <title>{isDE ? 'Seite nicht gefunden – Taxi Salzburg' : 'Page Not Found – Taxi Salzburg'}</title>
        <meta name="description" content={isDE ? 'Die gewünschte Seite wurde nicht gefunden. Buchen Sie Ihren Taxi-Transfer in Salzburg.' : 'The page you are looking for was not found. Book your taxi transfer in Salzburg.'} />
      </Helmet>

      <section className="py-24 md:py-32 bg-background min-h-[80vh] flex items-center">
        <div className="container max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Big 404 */}
            <div className="relative mb-8">
              <span className="text-[10rem] md:text-[14rem] font-display font-black text-muted/30 leading-none select-none">
                404
              </span>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-6xl">🚕</span>
              </div>
            </div>

            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isDE ? 'Seite nicht gefunden' : 'Page Not Found'}
            </h1>
            <p className="text-muted-foreground text-lg mb-10 max-w-xl mx-auto">
              {isDE
                ? 'Die gewünschte Seite existiert leider nicht. Aber wir bringen Sie gerne an Ihr Ziel!'
                : "The page you're looking for doesn't exist. But we're happy to take you where you need to go!"}
            </p>

            {/* Popular Links */}
            <div className="mb-10">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                {isDE ? 'Beliebte Seiten' : 'Popular Pages'}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-lg mx-auto">
                {popularLinks.map(link => (
                  <LocalizedLink
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl bg-card border border-border hover:border-primary/50 hover:shadow-card transition-all group text-sm"
                  >
                    <link.icon className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                    <span className="text-foreground font-medium">{link.label}</span>
                  </LocalizedLink>
                ))}
              </div>
            </div>

            {/* Contact CTA */}
            <div className="bg-card rounded-2xl border border-border p-6 max-w-md mx-auto">
              <p className="text-foreground font-semibold mb-4">
                {isDE ? 'Brauchen Sie Hilfe? Kontaktieren Sie uns!' : 'Need help? Contact us!'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+436606521071"
                  dir="ltr"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-gold text-primary-foreground font-semibold hover:scale-105 transition-transform text-sm"
                >
                  <Phone className="w-4 h-4" />
                  +43 660 6521071
                </a>
                <a
                  href="https://wa.me/436606521071"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors text-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default NotFound;
